import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Coffee, Clock, MapPin, Phone, Instagram, Facebook, ArrowLeft, Ticket, Star, CreditCard } from "lucide-react";

// Fiktive Demo-Mini-Website (DIMEDIAL) für „Robbies Kaffeeküche“ – erfundener Betrieb, keine echte fremde Seite.
const IMG = {
  interior: "/demo/cafe_interior.jpg",
  latte: "/demo/latte_art.jpg",
  cold: "/demo/cold_brew.jpg",
  cake: "/demo/cheesecake.jpg",
  logo: "/demo/cafe_logo.jpg",
};

const HOURS = [["Mo – Fr", "8 – 18 Uhr"], ["Samstag", "8 – 16 Uhr"], ["Sonntag", "Ruhetag"]];
const MENU = [
  { img: IMG.latte, name: "Flat White", desc: "Cremig, mild, eigene Röstung", price: "3,80 €" },
  { img: IMG.cold, name: "Zimt-Cold-Brew", desc: "Hausgemacht, 18 Std. kalt gezogen", price: "4,20 €" },
  { img: IMG.cake, name: "Kürbis-Cheesecake", desc: "Saisonal & hausgebacken", price: "4,50 €" },
];

export default function DemoWebsitePage() {
  useEffect(() => { document.title = "Robbies Kaffeeküche · Demo-Website von DIMEDIAL"; }, []);
  return (
    <div className="min-h-screen bg-[#FBF7EF] text-[#2A2622]" data-testid="demo-website-page">
      <div className="bg-[#2A2622] text-[#FBF7EF] text-center text-xs sm:text-sm py-2 px-4" data-testid="demo-website-banner">
        Beispiel-Website von <strong>DIMEDIAL</strong> · „Robbies Kaffeeküche“ ist ein fiktiver Demo-Betrieb
        <Link to="/portal" className="ml-3 inline-flex items-center gap-1 underline decoration-[#FDD105] decoration-2 underline-offset-4" data-testid="demo-website-back"><ArrowLeft size={13} /> Zurück zum Portal</Link>
      </div>

      <header className="relative overflow-hidden">
        <img src={IMG.interior} alt="Café-Innenraum" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#2A2622]/70 via-[#2A2622]/55 to-[#2A2622]/80" />
        <div className="relative max-w-4xl mx-auto px-6 py-24 sm:py-32 text-center text-[#FBF7EF]">
          <img src={IMG.logo} alt="Logo" className="mx-auto h-24 w-24 rounded-full shadow-xl ring-4 ring-[#FBF7EF]/20 object-cover" data-testid="demo-website-logo" />
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">Robbies Kaffeeküche</h1>
          <p className="mt-4 text-base sm:text-lg text-[#FBF7EF]/90">Dein Lieblingsplatz für Kaffee, Kuchen und gute Laune – mitten in Herford.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#FDD105] text-[#2A2622] px-5 py-2.5 text-sm font-semibold"><Coffee size={16} /> Eigene Röstung</span>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#FBF7EF]/10 border border-[#FBF7EF]/25 px-5 py-2.5 text-sm">Mo – Sa geöffnet</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16 sm:py-20 space-y-20">
        <section data-testid="demo-website-menu">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#B88E3E] font-semibold">Aus der Kaffeeküche</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl">Unsere Lieblinge</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-3">
            {MENU.map((m) => (
              <article key={m.name} className="rounded-3xl bg-white overflow-hidden shadow-sm border border-[#EEE7D8]">
                <img src={m.img} alt={m.name} className="h-44 w-full object-cover" />
                <div className="p-5">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-semibold text-lg">{m.name}</h3>
                    <span className="text-[#B88E3E] font-semibold">{m.price}</span>
                  </div>
                  <p className="mt-1 text-sm text-[#6B655C]">{m.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="grid gap-6 md:grid-cols-2" data-testid="demo-website-angebot">
          <div className="relative overflow-hidden rounded-3xl bg-[#2A2622] text-[#FBF7EF] p-7 flex flex-col justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#FDD105] font-semibold flex items-center gap-2"><Star size={13} className="fill-[#FDD105]" /> Angebot der Woche</p>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl leading-tight">Hausgemachter Zimt-Cold-Brew</h2>
              <p className="mt-3 text-sm text-[#FBF7EF]/80">18 Stunden kalt gezogen, mit einer Prise Zimt – diese Woche zum Kennenlernpreis. Solange der Vorrat reicht.</p>
            </div>
            <div className="mt-6 flex items-baseline gap-3">
              <span className="text-3xl font-semibold text-[#FDD105]">3,50 €</span>
              <span className="text-sm text-[#FBF7EF]/60 line-through">4,20 €</span>
            </div>
          </div>

          <div className="rounded-3xl bg-white p-7 border border-[#EEE7D8] shadow-sm flex flex-col justify-between" data-testid="demo-website-event">
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#B88E3E] font-semibold flex items-center gap-2"><Ticket size={13} /> Veranstaltung</p>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl leading-tight">Latte-Art-Abend</h2>
              <p className="mt-3 text-sm text-[#6B655C]">Lern vom Barista die schönsten Muster – mit Verkostung und kleinem Snack. Plätze sind begrenzt.</p>
              <p className="mt-4 text-sm font-medium text-[#2A2622]">Freitag · 19 Uhr · 24,00 € pro Person</p>
            </div>
            <Link to="/robbies-kaffeekueche" className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#2A2622] text-[#FBF7EF] px-5 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5" data-testid="demo-website-ticket">
              <Ticket size={16} /> Ticket sichern (Demo)
            </Link>
          </div>
        </section>

        <section className="rounded-3xl bg-[#FDF3DC] border border-[#F0E3C2] p-7 flex flex-col sm:flex-row sm:items-center gap-5" data-testid="demo-website-kundenkarte">
          <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-[#2A2622] text-[#FDD105]"><CreditCard size={26} /></div>
          <div className="flex-1">
            <h2 className="font-display text-xl sm:text-2xl">Digitale Kundenkarte</h2>
            <p className="mt-1.5 text-sm text-[#6B655C]">Jeder zehnte Kaffee geht aufs Haus. Stempel sammelst du digital aufs Handy – ganz ohne Kärtchen im Portemonnaie. Frag einfach beim nächsten Besuch danach.</p>
          </div>
        </section>

        <section className="grid gap-10 sm:grid-cols-2" data-testid="demo-website-info">
          <div className="rounded-3xl bg-white p-7 border border-[#EEE7D8] shadow-sm">
            <h2 className="font-display text-xl flex items-center gap-2"><Clock size={18} className="text-[#B88E3E]" /> Öffnungszeiten</h2>
            <dl className="mt-4 space-y-2 text-sm">
              {HOURS.map(([d, t]) => (
                <div key={d} className="flex justify-between border-b border-dashed border-[#EEE7D8] pb-2">
                  <dt className="text-[#6B655C]">{d}</dt><dd className="font-medium">{t}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="rounded-3xl bg-white p-7 border border-[#EEE7D8] shadow-sm">
            <h2 className="font-display text-xl flex items-center gap-2"><MapPin size={18} className="text-[#B88E3E]" /> So findest du uns</h2>
            <p className="mt-4 text-sm text-[#6B655C]">Marktstraße 7<br />32052 Herford</p>
            <p className="mt-4 text-sm flex items-center gap-2"><Phone size={15} className="text-[#B88E3E]" /> 05221 1412070</p>
            <div className="mt-5 flex gap-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#FBF7EF] border border-[#EEE7D8] px-4 py-2 text-sm"><Instagram size={15} /> @robbies.kaffeekueche</span>
              <span className="inline-flex items-center gap-2 rounded-full bg-[#FBF7EF] border border-[#EEE7D8] px-4 py-2 text-sm"><Facebook size={15} /> Robbies Kaffeeküche</span>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-[#2A2622] text-[#FBF7EF]/80 text-center text-xs py-8 px-6">
        <p>Robbies Kaffeeküche · Marktstraße 7 · 32052 Herford</p>
        <p className="mt-2">Diese Seite ist ein unverbindliches Website-Beispiel von <strong className="text-[#FDD105]">DIMEDIAL</strong> und gehört zur passwortlosen Portal-Demo.</p>
        <p className="mt-2 text-[#FBF7EF]/55">Impressum: Demo – fiktiver Betrieb. Alle Namen, Adressen, Preise und Angebote sind erfunden.</p>
      </footer>
    </div>
  );
}
